import React, { useEffect, useMemo, useState } from 'react';
import { X, ArrowRightLeft } from 'lucide-react';
import jobService from '../../services/jobService';
import {
  candidateActionsApi,
  JobContainingCv,
} from '../../services/candidateActionsApi';
import './CandidateToolbarModals.scss';

interface MoveCandidateModalProps {
  cvId: number;
  candidateName: string;
  onClose: () => void;
  onMoved?: (result: 'moved' | 'merged') => void;
}

interface EmployerJob {
  id: number;
  title: string;
}

/**
 * "Perkelti CV" — reassigns a candidate from one job's atranka to another.
 *
 * The modal pulls two lists in parallel:
 *  - jobs that already contain the candidate (source dropdown)
 *  - all employer jobs (target dropdown)
 *
 * Auto-selects the most recent source when there's only one.
 */
const MoveCandidateModal: React.FC<MoveCandidateModalProps> = ({
  cvId,
  candidateName,
  onClose,
  onMoved,
}) => {
  const [containingJobs, setContainingJobs] = useState<JobContainingCv[]>([]);
  const [allJobs, setAllJobs] = useState<EmployerJob[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fromJobId, setFromJobId] = useState<number | null>(null);
  const [toJobId, setToJobId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingLists(true);
        const [containing, all] = await Promise.all([
          candidateActionsApi.listJobsForCv(cvId),
          jobService.getEmployerJobs(),
        ]);
        if (cancelled) return;
        setContainingJobs(containing);
        setAllJobs(all);
        // Auto-pick the only source so the user just picks a target.
        if (containing.length === 1) {
          setFromJobId(containing[0].job_id);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Nepavyko įkelti skelbimų');
      } finally {
        if (!cancelled) setLoadingLists(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [cvId]);

  const containingIds = useMemo(
    () => new Set(containingJobs.map((j) => j.job_id)),
    [containingJobs],
  );
  // Targets = jobs the candidate isn't already in (avoid the no-op move).
  const targetCandidates = useMemo(
    () => allJobs.filter((j) => !containingIds.has(j.id) && j.id !== fromJobId),
    [allJobs, containingIds, fromJobId],
  );

  const handleSubmit = async () => {
    if (!fromJobId || !toJobId) {
      setError('Pasirinkite skelbimą iš kurio ir į kurį perkelti');
      return;
    }
    if (fromJobId === toJobId) {
      setError('Pradinis ir paskirties skelbimas turi skirtis');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const res = await candidateActionsApi.moveCv(cvId, fromJobId, toJobId);
      onMoved?.(res.result);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Nepavyko perkelti kandidato');
    } finally {
      setSubmitting(false);
    }
  };

  const noContainingJobs = !loadingLists && containingJobs.length === 0;

  return (
    <div className="candidate-toolbar-modal-overlay" onClick={submitting ? undefined : onClose}>
      <div
        className="candidate-toolbar-modal candidate-toolbar-modal--move"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="candidate-toolbar-modal__close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Uždaryti"
        >
          <X size={20} />
        </button>

        <div className="candidate-toolbar-modal__header">
          <ArrowRightLeft size={20} />
          <h2>Perkelti CV</h2>
        </div>

        <p className="candidate-toolbar-modal__subtitle">{candidateName}</p>

        {loadingLists ? (
          <div className="candidate-toolbar-modal__loading">Kraunama...</div>
        ) : noContainingJobs ? (
          <div className="candidate-toolbar-modal__hint">
            Šis kandidatas dar nėra įkeltas į jokio skelbimo atranką. Pirmiausia
            pridėkite jį per „Įkelti į atrankas“ ir tada galėsite perkelti.
          </div>
        ) : (
          <>
            <div className="candidate-toolbar-modal__field">
              <label>Iš kurio skelbimo</label>
              <select
                value={fromJobId ?? ''}
                onChange={(e) => setFromJobId(e.target.value ? Number(e.target.value) : null)}
                disabled={submitting}
              >
                <option value="">Pasirinkite skelbimą</option>
                {containingJobs.map((j) => (
                  <option key={j.job_id} value={j.job_id}>
                    {j.job_title}
                  </option>
                ))}
              </select>
            </div>

            <div className="candidate-toolbar-modal__field">
              <label>Į kurį skelbimą</label>
              <select
                value={toJobId ?? ''}
                onChange={(e) => setToJobId(e.target.value ? Number(e.target.value) : null)}
                disabled={submitting || !fromJobId}
              >
                <option value="">Pasirinkite skelbimą</option>
                {targetCandidates.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.title}
                  </option>
                ))}
              </select>
              {fromJobId && targetCandidates.length === 0 && (
                <p className="candidate-toolbar-modal__hint candidate-toolbar-modal__hint--inline">
                  Daugiau aktyvių skelbimų neturite, į kuriuos kandidatas dar nebūtų įkeltas.
                </p>
              )}
            </div>
          </>
        )}

        {error && <div className="candidate-toolbar-modal__error">{error}</div>}

        <div className="candidate-toolbar-modal__actions">
          <button
            type="button"
            className="candidate-toolbar-modal__btn candidate-toolbar-modal__btn--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            Atšaukti
          </button>
          <button
            type="button"
            className="candidate-toolbar-modal__btn candidate-toolbar-modal__btn--primary"
            onClick={handleSubmit}
            disabled={submitting || loadingLists || noContainingJobs || !fromJobId || !toJobId}
          >
            {submitting ? 'Perkeliama...' : 'Perkelti'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MoveCandidateModal;
